import { isEqual, omit, pick } from 'lodash';
import {
    LocationQuery,
    LocationQueryRaw,
    LocationQueryValue,
    LocationQueryValueRaw,
    NavigationFailure,
    RouteLocationNormalizedLoaded, RouteLocationRaw,
    Router,
    useRoute,
    useRouter,
} from 'vue-router';
import { computed, ComputedRef, nextTick, WritableComputedRef } from 'vue';

let pendingQuery: LocationQueryRaw | null = null;
let _nonFacetKeys: string[] = [];
let _invalidates: Record<string, string[]> = {};
let _valueFormats: Record<string, (value: any) => any> = {};

export function configure<T extends string>(nonFacetKeys: T[], invalidates: { [key: string]: T[] } = {}, valueFormats: { [key: string]: (value: any) => any } = {}): void {
    _nonFacetKeys = nonFacetKeys;
    _invalidates = invalidates;
    _valueFormats = valueFormats;
}

export function useRouteQuery<T extends LocationQueryValueRaw | LocationQueryValueRaw[] = string>(name: string, options: {
    replace: boolean,
} = {
    replace: true,
}): WritableComputedRef<T> {
    const $route = useRoute();
    const $router = useRouter();

    return computed<T>({
        get: () => {
            const value = $route.query[name];
            return _valueFormats[name] ? _valueFormats[name](value) : value as T;
        },
        set(value: T) {
            const newQuery = pendingQuery || queryClone($route.query);
            newQuery[name] = value;

            // Remove keys that are invalidated by this query-key
            const shouldBeRemoved = _invalidates[name] || [];
            shouldBeRemoved.forEach(key => delete newQuery[key]);

            // Queue and update once pr. batch
            if (!pendingQuery) {
                pendingQuery = newQuery;
                nextTick().then(async() => {
                    await updateQuery($router, $route, pendingQuery!, options.replace);
                    pendingQuery = null;
                });
            }
        },
    });
}

function createReactiveFacets(route: RouteLocationNormalizedLoaded) {
    return computed(() => {
        const { query } = route;
        return omit(queryClone(query), _nonFacetKeys);
    });
}

export function applyFacets(router: Router, route: RouteLocationNormalizedLoaded, facets: Record<string, string | string[] | undefined>) {
    // Clean up old facets and set new ones
    const { query } = route;
    const cloneWithoutFacets = pick(queryClone(query), _nonFacetKeys);
    const result = { ...cloneWithoutFacets, ...facets };
    return updateQuery(router, route, result, true);
}

export function useFacets(): {
    facets: ComputedRef<Record<string, string | string[]>>,
    applyFacets: (facets: Record<string, string | string[]>) => void
    } {
    const $router = useRouter();
    const $route = useRoute();

    return {
        facets: createReactiveFacets($route),
        applyFacets: (facets: Record<string, string | string[]>) => applyFacets($router, $route, facets),
    };
}

export function updateQuery(router: Router, route: RouteLocationNormalizedLoaded, query: LocationQueryRaw, replace: boolean): Promise<NavigationFailure | void | undefined> {
    if (isEqual(query, route.query)) {
        return Promise.resolve();
    }

    const method = replace ? router.replace : router.push;
    return method({ query, path: router.currentRoute.value.path });
}

function safeArrayifyFacetValue(facetValue: LocationQueryValue | LocationQueryValue[]): string[] {
    return !facetValue
        ? []
        : typeof facetValue === 'string'
            ? [facetValue]
            : [...facetValue] as string[];
}

function queryClone(query: LocationQuery): LocationQuery {
    if (!query) throw new Error('Illegal access to query');
    const clone = {
        ...query,
    };
    Object.keys(clone).forEach(key => {
        clone[key] = safeArrayifyFacetValue(clone[key]);
    });
    return clone;
}

export function qetQueryLink(queryUpdate: LocationQuery): RouteLocationRaw {
    const router = useRouter();
    const query = {
        ...router.currentRoute.value.query,
        ...queryUpdate,
    };
    const location = {
        path: router.currentRoute.value.path,
        query,
    };
    return location;
}

export function getRouteQueryFromFullPath(fullPath: string): LocationQuery {
    const paramsSplit = fullPath.split('?');
    if (paramsSplit.length > 1) {
        const params = new URLSearchParams(paramsSplit[1]);
        return Object.fromEntries(params.entries());
    } else {
        return {} as LocationQuery;
    }
}

export function asString(queryValue: LocationQueryValue | LocationQueryValue[]): string {
    const value = safeArrayifyFacetValue(queryValue)[0];
    return value?.toString() ?? '';
}

export function asBoolean(queryValue: LocationQueryValue | LocationQueryValue[]): boolean {
    const value = asString(queryValue);
    return value === 'true' || value === '1';
}

export function asInt(queryValue: LocationQueryValue | LocationQueryValue[]): number {
    const value = asString(queryValue);
    const parsedValue = parseInt(value, 10);
    return !isNaN(parsedValue) ? parsedValue : 0;
}

export function asNumber(queryValue: LocationQueryValue | LocationQueryValue[]): number {
    const value = asString(queryValue);
    const parsedValue = Number(value);
    return !isNaN(parsedValue) ? parsedValue : 0;
}
